import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { getFindingInvestigation } from "../lib/alertTriageClient.js";
import { loadPolicyDraft, policyDraftState } from "../lib/policyDraftStore.js";
import { investigationWriter } from "../lib/workspaceInvestigation.js";
import { discardWorkspacePolicy, retryWorkspacePolicySave, startWorkspacePolicy, subscribeWorkspacePolicy, workspacePolicyRevision, workspacePolicySession, showControlOptions } from "../lib/workspacePolicy.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { getDockChatBar, subscribeDock } from "../lib/dockStore.js";
import { traceCanvasOpen, traceCanvasSnapshot } from "../lib/traceCanvasStore.js";
import { getProposal, showCurrentProposal } from "../lib/controlProposalSession.js";
import Button from "./ui/Button.jsx";
import { ErrorText, SaveStatus } from "./ui/Notice.jsx";

export default function ControlProposalBar({ investigationId, onChat }) {
    const caps = useMyPermissions();
    const canRead = !!caps?.available && !caps.subjectScopeDegraded && hasPermission(caps, PERMISSIONS.AGENT_CONFIG_READ);
    useSyncExternalStore(subscribeWorkspacePolicy, workspacePolicyRevision, workspacePolicyRevision);
    const chatBar = useSyncExternalStore(subscribeDock, getDockChatBar, () => null);
    const activeChat = chatBar?.tabs?.find((tab) => tab.active);
    const chatId = activeChat?.chatId;
    const chatBusy = !!activeChat?.busy;
    const alive = useRef(true);
    const restoring = useRef(null);
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [discarding, setDiscarding] = useState(false);
    const state = policyDraftState(investigationId);
    const session = workspacePolicySession(investigationId);
    useEffect(() => {
        setError(""); setDiscarding(false);
        if (investigationId && canRead) loadPolicyDraft(investigationId);
    }, [investigationId, canRead]);
    const isCurrent = () => alive.current && traceCanvasSnapshot(traceCanvasOpen()?.id)?.investigationId === investigationId;
    useEffect(() => {
        if (!canRead || !chatBar?.hydrated || chatBusy || !state.draft?.id || chatId == null || workspacePolicySession(investigationId) || !isCurrent()) return undefined;
        let cancelled = false;
        setBusy(true);
        const pending = getFindingInvestigation(investigationId)
            .then((record) => cancelled ? null : startWorkspacePolicy(investigationWriter(investigationId, record), { isCurrent: () => !cancelled && isCurrent() }))
            .then((opened) => { if (opened && !cancelled) showCurrentProposal(opened.sessionId, { view: getProposal(opened.sessionId)?.view || "review" }); })
            .catch((failure) => { if (!cancelled) setError(failure.message || "Could not resume this local draft. Retry."); })
            .finally(() => { if (!cancelled) setBusy(false); if (restoring.current === pending) restoring.current = null; });
        restoring.current = pending;
        return () => { cancelled = true; };
    }, [investigationId, canRead, state.draft?.id, chatId, chatBusy, chatBar?.hydrated]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!canRead || !investigationId || (!session && !state.draft && !state.error)) return null;
    const run = async (fn) => {
        setBusy(true); setError("");
        try { await fn(); } catch (failure) { setError(failure.message || "Could not open this proposal. Retry."); }
        finally { setBusy(false); }
    };
    const review = () => run(async () => {
        if (!isCurrent()) throw new Error("The workspace changed. Reopen the proposal from its investigation.");
        await restoring.current;
        let current = workspacePolicySession(investigationId);
        if (!current || state.status === "conflict") {
            const record = await getFindingInvestigation(investigationId);
            const opened = await startWorkspacePolicy(investigationWriter(investigationId, record), { reload: true, isCurrent });
            current = getProposal(opened.sessionId);
        }
        if (!isCurrent() || !current) throw new Error("Reopen the current investigation to review its draft.");
        showCurrentProposal(current.id, { reveal: true });
        onChat();
        requestAnimationFrame(() => document.getElementById(`control-proposal-${current.id}`)?.scrollIntoView({ block: "nearest" }));
    });
    return <div className="flex-none border-b border-[color:var(--line-soft)] px-[12px] py-[7px] text-[12px]" aria-label="Control proposal" aria-busy={busy} data-control-proposal-bar="">
        <div className="flex flex-wrap items-center gap-x-[8px] gap-y-[5px]">
            <span className="font-medium">Control proposal</span>
            <span className="text-[color:var(--muted)]">{session?.view === "starting" ? "Starting pilots" : chatBusy ? "Preparing" : session?.started?.length || state.draft?.submitted ? "Submitted" : "Draft"}</span>
            <SaveStatus status={state.status} savedLabel="Saved in this browser" />
            <Button size="sm" variant="ghost" className="ml-auto" disabled={busy || chatBusy || session?.view === "starting"} onClick={review}>{state.status === "conflict" ? "Reload proposal" : "Review draft"}</Button>
            <Button size="sm" variant="ghost" disabled={busy || chatBusy || session?.view === "starting"} onClick={() => { showControlOptions(); onChat(); }}>Add controls</Button>
            <Button size="sm" variant="ghost" disabled={busy || session?.view === "starting"} onClick={() => setDiscarding(!discarding)}>Discard</Button>
        </div>
        {discarding ? <div className="mt-[8px] flex flex-wrap items-center gap-[8px]">
            <span>Discard this browser's draft? Existing pilots are unaffected.</span>
            <Button size="sm" variant="danger" disabled={busy} onClick={() => run(() => discardWorkspacePolicy(investigationId))}>Discard local draft</Button>
            <Button size="sm" variant="ghost" onClick={() => setDiscarding(false)}>Keep draft</Button>
        </div> : null}
        <ErrorText>{error || state.error}</ErrorText>
        {state.status === "error" ? <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => session ? retryWorkspacePolicySave(investigationId) : loadPolicyDraft(investigationId, { force: true }))}>Retry local save</Button> : null}
    </div>;
}
